import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useMaxUiKpis } from "../common/hooks/useMaxUiKpis";
import { getCanonicalTitle } from "../../../common/util/Incentives/IncentiveRegistry";
import { SELECTOR_TYPE, updateSelectorType } from "../../../reducers/period";
import { BonusInfoFooter } from "../../../common/components/BonusInfoFooter/BonusInfoFooter";
import { ActionReportHeader } from "../common/Header/ActionReportHeader";
import { ActionReportToolbar } from "../common/Toolbar/ActionReportToolbar";
import { ActionReportListView, ActionReportListViewLoader } from "../common/ActionReportListView/ActionReportListView";
import { ActionReportsTreeView } from "../common/ActionReportTreeView/ActionReportsTreeView";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { ERROR_TYPE, ErrorPage, IconType } from "../../ErrorPage/ErrorPage";
import { FetchCvrActionReportData } from "./CvrActionReportDataLayer";
import { toggleSortDirection, setAvailableKpis, setAffAbosToExpand } from "../../../reducers/cvrArReport";
import { useActionReportTreeView } from "../common/hooks/useActionReportTreeView";
import defaultContent from "../../../content/actionReportsCvr";
import { MODULE_CVR_V2 } from "../../../common/constants/export-constants";
import ServerSideExportInProgressNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import ServerSideExportCompletedNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import { dispatchOpenExportV2ModalEvent } from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { buildFieldTitleOverrides } from "../../../common/util/exportUtil";
import { LeafModal } from "../../../common/leaf/LeafModal/LeafModal";
import { IconInfoCircle } from "../../../common/icons/IconInfoCircle/IconInfoCircle";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import type {
  SearchSelectItem,
  SearchCallback,
  SelectionCallback,
} from "../../../common/components/SearchSelect/SearchSelect";
import {
  cvrPageViewTrack,
  cvrCustomizeKpiTrack,
  cvrCancelCustomizeKpiTrack,
  cvrApplyCustomizeKpiTrack,
  cvrSortAscendingTrack,
  cvrSortDescendingTrack,
  cvrExportTrack,
  cvrProfileTrack,
  cvrAddToWatchlistTrack,
  cvrRemoveFromWatchlistTrack,
  cvrInfoTrack,
  cvrPhoneIconTrack,
  cvrMessageIconTrack,
} from "../../../common/tealium/ActionReports/CvrComplianceTrack";
import "../ActionReports.scss";
import "./CvrActionReport.scss";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";
import { storedLocale } from "../../Authentication/util";
import { useGetSavedKpiPreferenceQuery } from "../../../services/KPIPreferences/savedKpiPreferencesAPI";

const DEFAULT_KPIS = [KPI_ID.VCS_PV, KPI_ID.NON_VCS_PV, KPI_ID.PERSONAL_PV, KPI_ID.BONUS_PERCENT];

/**
 * Creates field configuration for KPI columns in the action report table.
 */
const createFieldConfig = (kpiIds: string[], tableColumns: { [key: string]: string }) => [
  ...kpiIds.map((kpiId) => ({
    id: kpiId,
    kpi: kpiId,
    title: tableColumns[kpiId] || kpiId,
  })),
  { id: KPI_ID.PHONE_NUMBER, kpi: "phoneNumber", title: "" },
  { id: KPI_ID.WHATSAPP, kpi: "whatsapp", title: "" },
];

const useSelectedKpis = (configuredKpis: string[]) => {
  const { savedKpiPreferencesData } = useAppSelector((state) => state.savedKpiPreferencesData);
  useGetSavedKpiPreferenceQuery("cvrKPIs", { refetchOnMountOrArgChange: true });

  return useMemo(() => {
    const savedView = savedKpiPreferencesData?.savedDataViews?.cvrKPIs;
    if (savedView) {
      return savedView.availableKpis
        .filter((kpi: { displaydefault: boolean; id: string }) => kpi.displaydefault && configuredKpis.includes(kpi.id))
        .map((kpi: { id: string }) => kpi.id);
    }
    return DEFAULT_KPIS.filter((kpi) => configuredKpis.includes(kpi));
  }, [savedKpiPreferencesData, configuredKpis]);
};

export default function CvrActionReportV2() {
  const { t } = useTranslation(["actionReportsCvr", "leafAboName"]);
  const maxUiKpis = useMaxUiKpis();
  const dispatch = useAppDispatch();
  const { configuration } = useAppSelector(({ boot }) => boot);
  const { uiKpis } = configuration;
  const { sortDirection, availableKpis } = useAppSelector((state) => state.cvrActionReportTracker);
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const confidentialUser = t("ConfidentialUser", "Confidential User", { ns: "leafAboName" });

  useEffect(() => {
    dispatch(updateSelectorType(SELECTOR_TYPE.MONTHLY));
  }, [dispatch]);

  // Use market config for CVR KPIs, fallback to defaults
  const cvrKpiConfig: string[] = uiKpis?.cvrAr || DEFAULT_KPIS;

  const { cvrActionReportData, isLoading, errorStatus } = FetchCvrActionReportData(selectedPeriod);

  const hasData = !isLoading && !errorStatus && (cvrActionReportData.results || []).length > 0;

  const allAbos = useMemo(
    () => (cvrActionReportData.results || []).map((record) => ({ aff: String(record.aff), abo: String(record.abo) })),
    [cvrActionReportData.results],
  );

  const { isTreeView, handleToggle } = useActionReportTreeView({
    isLoading,
    hasData,
    setAffAbosToExpand,
    allAbos,
  });

  // Search state
  const [searchText, setSearchText] = useState<string>("");
  const [scrollToAbo, setScrollToAbo] = useState<{ aff: string; abo: string } | undefined>(undefined);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    dispatch(setAvailableKpis(cvrKpiConfig));
  }, [dispatch, cvrKpiConfig]);

  useEffect(() => {
    cvrPageViewTrack(selectedPeriod);
  }, [selectedPeriod]);

  const handleSortClick = () => {
    if (sortDirection === "asc") {
      cvrSortDescendingTrack();
    } else {
      cvrSortAscendingTrack();
    }
    dispatch(toggleSortDirection());
  };

  const handleWatchlistTrack = (isAdding: boolean) => {
    if (isAdding) {
      cvrAddToWatchlistTrack();
    } else {
      cvrRemoveFromWatchlistTrack();
    }
  };

  const tableColumns = t("table.columns", {
    ns: "actionReportsCvr",
    returnObjects: true,
    defaultValue: defaultContent.table.columns,
  }) as { [key: string]: string };

  const selectedKpis = useSelectedKpis(cvrKpiConfig);
  const fieldConfig = useMemo(() => createFieldConfig(cvrKpiConfig, tableColumns), [cvrKpiConfig, tableColumns]);

  // Sort the results based on sortDirection
  const sortedResults = useMemo(() => {
    const results = cvrActionReportData.results || [];
    if (sortDirection === "none") return results;
    return sortDirection === "asc" ? [...results].reverse() : results;
  }, [cvrActionReportData.results, sortDirection]);

  // Create search pool from all ABOs
  const searchPool = useMemo(() => {
    return sortedResults.map((record, index) => ({
      abo: String(record.abo),
      aff: String(record.aff),
      isoCountry: record.isoCountry,
      aboName: record.confidentialUser ? confidentialUser : record.aboName || "",
      index,
    }));
  }, [sortedResults, confidentialUser]);

  const handleSearchCallback = useCallback<SearchCallback>(
    (text) =>
      searchPool
        .filter((entry) => entry.aboName.toLowerCase().includes(text.toLowerCase()) || entry.abo.includes(text))
        .map((entry): SearchSelectItem => {
          const sublabel = `#${entry.abo} | ${getCountryNameFromCode(entry.isoCountry, storedLocale())}`;
          return {
            key: `${entry.aff}-${entry.abo}`,
            label: entry.aboName,
            sublabel,
          };
        }),
    [searchPool],
  );

  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => {
      const found = searchPool.find((entry) => `${entry.aff}-${entry.abo}` === key);
      if (found) {
        setScrollToAbo({ aff: found.aff, abo: found.abo });
        setSearchText(found.aboName);
      }
      return { displayName: found?.aboName ?? "" };
    },
    [searchPool],
  );

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setScrollToAbo(undefined);
  }, []);

  const totalAboCount = sortedResults.length;

  const exportAffAbos = useMemo(() => {
    return sortedResults.map((record) => ({
      affAbo: `${record.aff}-${record.abo}`,
    }));
  }, [sortedResults]);

  const startedByLabels = useMemo<Record<string, string>>(
    () => ({
      [MODULE_CVR_V2]: t("export.exportTypeLabel", defaultContent.export.exportTypeLabel).toString(),
    }),
    [t],
  );

  const fieldTitleOverrides = useMemo(() => buildFieldTitleOverrides(tableColumns), [tableColumns]);

  // Find scrollToIndex based on search
  const scrollToIndex = useMemo(() => {
    if (!scrollToAbo) return undefined;
    const index = sortedResults.findIndex(
      (record) => String(record.aff) === scrollToAbo.aff && String(record.abo) === scrollToAbo.abo,
    );
    return index === -1 ? undefined : index;
  }, [scrollToAbo, sortedResults]);

  const renderContent = () => {
    if (isTreeView) {
      return <ActionReportsTreeView path="cvr" />;
    }

    if (isLoading) {
      return (
        <div className="CvrAR__listViewWrapper">
          <ActionReportListViewLoader />
        </div>
      );
    }

    if (errorStatus) {
      return (
        <div className="CvrAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.GENERIC_ERROR} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    if (sortedResults.length === 0) {
      return (
        <div className="CvrAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    const reportData = sortedResults.map((record) => ({
      aboName: record.confidentialUser ? confidentialUser : record.aboName,
      aboNumber: String(record.abo),
      abo: String(record.abo),
      aff: String(record.aff),
      level: record.level,
      imageRefUrl: record.imageRefUrl,
    }));

    return (
      <div className="CvrAR__listViewWrapper">
        <ActionReportListView
          selectedKpis={selectedKpis.filter((kpi) => availableKpis.includes(kpi))}
          reportId="cvrAR"
          reportData={reportData}
          fieldConfig={fieldConfig}
          enableWatchlist={true}
          scrollToIndex={scrollToIndex}
          onProfileTrack={cvrProfileTrack}
          onWatchlistTrack={handleWatchlistTrack}
          onPhoneTrack={cvrPhoneIconTrack}
          onMessageTrack={cvrMessageIconTrack}
          showImageBlock={true}
        />
      </div>
    );
  };

  const pageTitle = getCanonicalTitle({ id: "cvrCompliance" }, t, {});

  return (
    <main className="CvrAR">
      <ActionReportHeader
        title={pageTitle}
        description={
          isLoading ? (
            <span className="CvrAR__descriptionLoading">
              <span className="CvrAR__descriptionLoader">
                <LeafRectangleLoader isLoading={true} width="2rem" height="0.75rem" />
              </span>
              {t("title.reportCount", defaultContent.title.reportCount).toString()}
            </span>
          ) : (
            <span>
              <strong>{totalAboCount}</strong> {t("title.reportCount", defaultContent.title.reportCount).toString()}
            </span>
          )
        }
        reportLength={totalAboCount}
        infoButton={
          <button
            type="button"
            onClick={() => {
              cvrInfoTrack();
              setShowInfoModal(true);
            }}
            className="CvrAR__infoButton"
            aria-label={t("info.ariaLabel", defaultContent.info.ariaLabel).toString()}
          >
            <IconInfoCircle />
          </button>
        }
      />
      <ActionReportToolbar
        reportLength={totalAboCount}
        isAllEnriched={!isLoading}
        treeViewToggle={{
          enabled: true,
          isTreeView: isTreeView,
          onToggle: handleToggle,
          disabled: !hasData,
          label: t("losTreeView", defaultContent.losTreeView) as string,
        }}
        sort={{
          enabled: !isTreeView,
          direction: sortDirection === "none" ? "desc" : sortDirection,
          onClick: handleSortClick,
          disabled: isLoading || totalAboCount === 0,
        }}
        kpiSelector={
          !isTreeView
            ? {
                availableKpis: cvrKpiConfig,
                defaultKpis: DEFAULT_KPIS,
                saveViewName: "cvrKPIs",
                saveViewDescription: "CVR Compliance KPI Preferences",
                maxSelectionsAllowed: maxUiKpis,
                textOverrides: tableColumns,
                onOpenTrack: cvrCustomizeKpiTrack,
                onCancelTrack: cvrCancelCustomizeKpiTrack,
                onApplyTrack: cvrApplyCustomizeKpiTrack,
                disabled: isLoading || totalAboCount === 0,
              }
            : undefined
        }
        search={
          !isTreeView
            ? {
                enabled: true,
                searchCallback: handleSearchCallback,
                selectionCallback: handleSelectionCallback,
                searchText: searchText,
                clearFunction: handleClearSearch,
                disabled: isLoading || searchPool.length === 0,
              }
            : undefined
        }
        export={{
          moduleId: MODULE_CVR_V2,
          exportAffAbos: exportAffAbos,
          selectedKpis: selectedKpis.filter((kpi) => cvrKpiConfig.includes(kpi)),
          confirmationMessage: t("export.description", defaultContent.export.description).toString(),
          exportType: MODULE_CVR_V2,
          startedByLabels,
          fieldTitleOverrides,
        }}
        exportTrack={cvrExportTrack}
      />
      <div className="CvrAR__content">{renderContent()}</div>
      <div>
        <BonusInfoFooter />
      </div>
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify
        moduleId={MODULE_CVR_V2}
        notifyDownloadLinkOnClick={() => dispatchOpenExportV2ModalEvent(MODULE_CVR_V2, true)}
      />
      <LeafModal
        open={showInfoModal}
        onCloseCallbackFn={() => setShowInfoModal(false)}
        title={t("title.pageTitle", defaultContent.title.pageTitle).toString()}
        id="cvrInfoModal"
      >
        <div className="CvrAR__infoModal">
          <p className="CvrAR__infoModal__description">
            {t("title.pageDescription", defaultContent.title.pageDescription).toString()}
          </p>
        </div>
      </LeafModal>
    </main>
  );
}
